import type { AnyFieldApi, Updater } from '@tanstack/react-form';
import type { FormComponentErrorMode, FormComponentProps } from './types.ts';
import { useSelector } from '@tanstack/react-form';
import { omit } from 'es-toolkit';
import { useCallback } from 'react';

export function usePropsFromField<T extends FormComponentProps<{ disabled?: boolean }>>(
  props: T,
  field: AnyFieldApi,
  defaultErrorMode: FormComponentErrorMode = 'blurred'
): Omit<T, 'errorMode' | 'showErrorMessage'> & {
  disabled: boolean;
  error: string | boolean | undefined;
} {
  const { errorMode = defaultErrorMode, showErrorMessage = true } = props;

  const disabled = useSelector(field.form.store, state => state.isSubmitting);

  let error: string | boolean | undefined = getFieldError(field, errorMode);

  if (error && !showErrorMessage) {
    error = true;
  }

  return {
    ...stripBaseFormComponentProps(props),
    disabled: props.disabled ?? disabled,
    error
  };
}

export function stripBaseFormComponentProps<T extends FormComponentProps<unknown>>(
  props: T
): Omit<T, 'errorMode' | 'showErrorMessage'> {
  return omit(props, ['errorMode', 'showErrorMessage']);
}

export function getFieldError(
  field: {
    form: Pick<AnyFieldApi['form'], 'state'>;
    state: AnyFieldApi['state'];
  },
  errorMode: FormComponentErrorMode = 'blurred'
): string | boolean | undefined {
  if (
    field.form.state.submissionAttempts > 0 ||
    errorMode === 'always' ||
    (errorMode === 'blurred' && field.state.meta.isBlurred) ||
    (errorMode === 'dirty' && field.state.meta.isDirty)
  ) {
    const error = field.state.meta.errors[0];
    return renderFormError(error);
  }
}

export function renderFormError(error: unknown): string | boolean | undefined {
  if (typeof error === 'undefined' || error === null) {
    return undefined;
  }

  if (typeof error === 'string' || typeof error === 'boolean') {
    return error;
  }

  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }

  throw new Error('unknown error in form', {
    cause: error
  });
}

export function useMergeHandleChangeAndBlur<TData>(
  handleChange: (updater: Updater<TData>) => void,
  handleBlur: () => void
) {
  return useCallback(
    (updater: Updater<TData>) => {
      handleChange(updater);
      handleBlur();
    },
    [handleChange, handleBlur]
  );
}
